/**
 * Nav — navigation representation baked from the level at load.
 *
 * The bake is a heightfield voxelisation of `ctx.level.navBounds`:
 *
 *   1. one downward `raycastAll` per XZ column finds every candidate floor span
 *      (true triangle normal within the slope limit, head clearance >= 1.85 m),
 *   2. one `overlapCapsule` per span rejects spans an agent cannot stand in,
 *   3. spans in the 8-neighbourhood are linked when the height difference is
 *      inside the step limit, with corner-cutting suppressed on diagonals,
 *   4. off-mesh links (vault / drop) are added where a walk link cannot exist
 *      but a traversal obviously can,
 *   5. edge spans (spans with a blocked neighbour) are probed with 8 x 2 short
 *      horizontal rays and classified as low or high cover per direction.
 *
 * Everything lives in flat typed arrays. Runtime queries are A* + string-pull,
 * and cover selection is a grid lookup over the baked cover masks.
 *
 * owner: ai
 */
import * as THREE from 'three';

export const AGENT_RADIUS = 0.35;
export const AGENT_HEIGHT = 1.80;
export const MAX_STEP = 0.45;
export const MAX_SLOPE = 50 * Math.PI / 180;
export const MIN_CLEARANCE = 1.85;
/** Heuristic inflation for the runtime A*. 1 = optimal, higher = greedier. */
export const HEURISTIC_W = 1.35;
/** How far ahead the string-puller may skip in one hop. */
export const SMOOTH_LOOKAHEAD = 20;
/** Longest raw node chain the smoother will ever look at. */
export const MAX_RAW_NODES = 90;
/** Height a vault obstacle must be clear above for the link to exist. */
export const VAULT_CLEAR = 1.25;

/** Link kinds. Anything > LINK_WALK is an off-mesh link. */
export const LINK_WALK = 0;
export const LINK_VAULT = 1;
export const LINK_DROP = 2;

/** Cover is binned into 8 compass directions. Bit d = "protected from d". */
export const COVER_DIRS = 8;
const DIR_X = new Float32Array(COVER_DIRS);
const DIR_Z = new Float32Array(COVER_DIRS);
for (let d = 0; d < COVER_DIRS; d++) {
  const a = (d / COVER_DIRS) * Math.PI * 2;
  DIR_X[d] = Math.sin(a);
  DIR_Z[d] = Math.cos(a);
}
/** Direction bin of a world vector (the direction a threat lies in). */
export function dirBin(dx, dz) {
  const a = Math.atan2(dx, dz);
  let d = Math.round((a / (Math.PI * 2)) * COVER_DIRS);
  d %= COVER_DIRS;
  if (d < 0) d += COVER_DIRS;
  return d;
}

const _v = new THREE.Vector3();
const _o = new THREE.Vector3();
const _d = new THREE.Vector3();

/* ------------------------------------------------------------------ *
 * a small binary heap over node ids, keyed by f-score
 * ------------------------------------------------------------------ */
class Heap {
  constructor(cap) {
    this.id = new Int32Array(cap);
    this.key = new Float32Array(cap);
    this.n = 0;
    this.cap = cap;
  }
  clear() { this.n = 0; }
  push(id, key) {
    if (this.n >= this.cap) return;
    let i = this.n++;
    this.id[i] = id; this.key[i] = key;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.key[p] <= this.key[i]) break;
      this._swap(p, i); i = p;
    }
  }
  pop() {
    if (this.n === 0) return -1;
    const top = this.id[0];
    this.n--;
    if (this.n > 0) {
      this.id[0] = this.id[this.n]; this.key[0] = this.key[this.n];
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        if (l < this.n && this.key[l] < this.key[m]) m = l;
        if (r < this.n && this.key[r] < this.key[m]) m = r;
        if (m === i) break;
        this._swap(m, i); i = m;
      }
    }
    return top;
  }
  _swap(a, b) {
    const i = this.id[a]; this.id[a] = this.id[b]; this.id[b] = i;
    const k = this.key[a]; this.key[a] = this.key[b]; this.key[b] = k;
  }
}

/* ------------------------------------------------------------------ */

export class NavGraph {
  constructor() {
    this.ready = false;
    this.count = 0;
    this.cell = 0.6;
    this.gx = 0; this.gz = 0;
    this.minX = 0; this.minZ = 0;

    this.px = null; this.py = null; this.pz = null;   // floor point of each span
    this.col = null;                                   // column index of each span
    this.adjStart = null; this.adjNode = null; this.adjCost = null; this.adjKind = null;
    this.colStart = null; this.colNode = null;         // spans sorted by column
    this.coverLow = null; this.coverHigh = null;       // Uint8 direction masks
    this.flags = null;                                 // 1 = edge span

    this.stats = {
      bakeMs: 0, rayMs: 0, capsuleMs: 0, linkMs: 0, coverMs: 0,
      columns: 0, spans: 0, nodes: 0, links: 0, offMesh: 0, coverPoints: 0,
      rays: 0, capsules: 0,
    };

    // A* scratch
    this._g = null; this._f = null; this._prev = null; this._stamp = null;
    this._epoch = 0;
    this._heap = null;
    this._queryMs = 0;
    this._queries = 0;
  }

  /* ---------------------------------------------------------------- *
   * bake
   * ---------------------------------------------------------------- */

  /**
   * @param {object} physics   ctx.physics
   * @param {THREE.Box3} bounds  ctx.level.navBounds
   * @param {{cell?:number, maxNodes?:number}} [opts]
   */
  bake(physics, bounds, opts = {}) {
    const t0 = now();
    const cell = this.cell = opts.cell ?? 0.6;
    const MASK = physics.MASK ? physics.MASK.WORLD : 1;
    const minY = bounds.min.y - 1.5;
    const maxY = bounds.max.y + 2.5;
    const far = maxY - minY;
    const cosSlope = Math.cos(MAX_SLOPE);

    const minX = this.minX = bounds.min.x;
    const minZ = this.minZ = bounds.min.z;
    const gx = this.gx = Math.max(1, Math.floor((bounds.max.x - minX) / cell));
    const gz = this.gz = Math.max(1, Math.floor((bounds.max.z - minZ) / cell));
    const maxNodes = opts.maxNodes ?? 40000;

    const bvhNrm = physics.bvh && physics.bvh.nrm ? physics.bvh.nrm : null;

    // --- pass 1: floor spans -------------------------------------------------
    const sx = [], sy = [], sz = [], scol = [];
    _d.set(0, -1, 0);
    let tRay = 0, rays = 0;
    const tr0 = now();
    for (let i = 0; i < gx; i++) {
      const x = minX + (i + 0.5) * cell;
      for (let j = 0; j < gz; j++) {
        const z = minZ + (j + 0.5) * cell;
        _o.set(x, maxY, z);
        const hits = physics.raycastAll(_o, _d, far, MASK);
        rays++;
        if (!hits || hits.length === 0) continue;
        const colIdx = i * gz + j;
        let lastFloorY = Infinity;
        for (let k = 0; k < hits.length; k++) {
          const h = hits[k];
          // Physics flips normals toward the ray, so recover the geometric one.
          let ny;
          if (bvhNrm && h.triangle >= 0) ny = bvhNrm[h.triangle * 3 + 1];
          else ny = (k & 1) === 0 ? 1 : -1;   // closed-solid parity fallback
          if (ny < cosSlope) continue;
          const y = h.point.y;
          if (y < minY || y > bounds.max.y) continue;
          // head clearance: the first thing above this surface
          let ceil = Infinity;
          for (let q = k - 1; q >= 0; q--) {
            const cy = hits[q].point.y;
            if (cy - y > 0.06) { ceil = cy; break; }
          }
          if (ceil - y < MIN_CLEARANCE) continue;
          if (lastFloorY - y < 0.45) continue;   // same slab, sampled twice
          lastFloorY = y;
          sx.push(x); sy.push(y); sz.push(z); scol.push(colIdx);
          if (sx.length >= maxNodes) break;
        }
        if (sx.length >= maxNodes) break;
      }
      if (sx.length >= maxNodes) break;
    }
    tRay = now() - tr0;
    this.stats.columns = gx * gz;
    this.stats.spans = sx.length;
    this.stats.rays = rays;

    // --- pass 2: can an agent stand here? ------------------------------------
    const tc0 = now();
    const keep = new Uint8Array(sx.length);
    const hh = (AGENT_HEIGHT - 2 * AGENT_RADIUS) * 0.5;   // 0.55
    const testR = 0.32;                                    // slightly under the
    const lift = AGENT_RADIUS + hh + 0.06;                 // agent radius so
    let kept = 0, caps = 0;                                // cover hugs walls
    for (let n = 0; n < sx.length; n++) {
      _v.set(sx[n], sy[n] + lift, sz[n]);
      caps++;
      if (physics.overlapCapsule(_v, hh, testR, MASK)) continue;
      keep[n] = 1; kept++;
    }
    this.stats.capsuleMs = now() - tc0;
    this.stats.capsules = caps;

    // compact
    const count = this.count = kept;
    const px = this.px = new Float32Array(count);
    const py = this.py = new Float32Array(count);
    const pz = this.pz = new Float32Array(count);
    const col = this.col = new Int32Array(count);
    for (let n = 0, w = 0; n < sx.length; n++) {
      if (!keep[n]) continue;
      px[w] = sx[n]; py[w] = sy[n]; pz[w] = sz[n]; col[w] = scol[n]; w++;
    }

    // --- column index --------------------------------------------------------
    const nCols = gx * gz;
    const colStart = this.colStart = new Int32Array(nCols + 1);
    for (let n = 0; n < count; n++) colStart[col[n] + 1]++;
    for (let c = 0; c < nCols; c++) colStart[c + 1] += colStart[c];
    const colNode = this.colNode = new Int32Array(count);
    {
      const cursor = colStart.slice(0, nCols);
      for (let n = 0; n < count; n++) colNode[cursor[col[n]]++] = n;
    }

    // --- pass 3: links -------------------------------------------------------
    const tl0 = now();
    const NB = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
    const heads = new Int32Array(count).fill(-1);
    const nodeA = [], nodeB = [], costL = [], kindL = [];
    const walkable = (i, j, y) => this._spanNear(i, j, y, MAX_STEP);

    for (let n = 0; n < count; n++) {
      const c = col[n];
      const ci = (c / gz) | 0, cj = c % gz;
      const y = py[n];
      for (let k = 0; k < 8; k++) {
        const ni = ci + NB[k][0], nj = cj + NB[k][1];
        if (ni < 0 || nj < 0 || ni >= gx || nj >= gz) continue;
        const diag = k >= 4;
        if (diag) {
          // no corner cutting: both orthogonal cells must be reachable
          if (walkable(ci + NB[k][0], cj, y) < 0) continue;
          if (walkable(ci, cj + NB[k][1], y) < 0) continue;
        }
        const cc = ni * gz + nj;
        for (let s = colStart[cc]; s < colStart[cc + 1]; s++) {
          const m = colNode[s];
          const dy = py[m] - y;
          if (Math.abs(dy) > MAX_STEP) continue;
          const dx = px[m] - px[n], dz = pz[m] - pz[n];
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          nodeA.push(n); nodeB.push(m); costL.push(dist); kindL.push(LINK_WALK);
        }
      }
      void heads;
    }

    // off-mesh: vaults across a single blocked cell, and one-way ledge drops
    let offMesh = 0, vaultRays = 0;
    const VNB = [[2, 0], [-2, 0], [0, 2], [0, -2]];
    for (let n = 0; n < count; n++) {
      const c = col[n];
      const ci = (c / gz) | 0, cj = c % gz;
      const y = py[n];
      for (let k = 0; k < 4; k++) {
        const mi = ci + VNB[k][0] * 0.5, mj = cj + VNB[k][1] * 0.5;
        const ni = ci + VNB[k][0], nj = cj + VNB[k][1];
        if (ni < 0 || nj < 0 || ni >= gx || nj >= gz) continue;
        // middle cell must NOT be walkable, otherwise a normal link covers it
        if (this._spanNear(mi | 0, mj | 0, y, MAX_STEP) >= 0) continue;
        const m = this._spanNear(ni, nj, y, MAX_STEP + 0.25);
        if (m < 0) continue;
        const dx = px[m] - px[n], dz = pz[m] - pz[n], dy = py[m] - y;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        // A vault needs a *vaultable* obstacle. Without this test every wall
        // with floor on both sides sprouts a link straight through it: one ray
        // at chest height says whether a man could get his hips over it.
        _o.set(px[n], y + VAULT_CLEAR, pz[n]);
        _d.set(dx, 0, dz);
        const flat = Math.hypot(dx, dz);
        if (flat < 1e-3) continue;
        _d.multiplyScalar(1 / flat);
        if (physics.raycast(_o, _d, flat, MASK)) continue;
        vaultRays++;
        nodeA.push(n); nodeB.push(m); costL.push(dist + 2.6); kindL.push(LINK_VAULT);
        offMesh++;
      }
      // ledge drop straight down onto a lower span in a neighbouring column
      for (let k = 0; k < 4; k++) {
        const ni = ci + NB[k][0], nj = cj + NB[k][1];
        if (ni < 0 || nj < 0 || ni >= gx || nj >= gz) continue;
        const cc = ni * gz + nj;
        let best = -1, bestDy = -Infinity;
        for (let s = colStart[cc]; s < colStart[cc + 1]; s++) {
          const m = colNode[s];
          const dy = py[m] - y;
          if (dy > -MAX_STEP || dy < -3.4) continue;
          if (dy > bestDy) { bestDy = dy; best = m; }
        }
        if (best < 0) continue;
        const dx = px[best] - px[n], dz = pz[best] - pz[n];
        const dist = Math.sqrt(dx * dx + dz * dz) + Math.abs(bestDy);
        nodeA.push(n); nodeB.push(best); costL.push(dist + 1.8); kindL.push(LINK_DROP);
        offMesh++;
      }
    }

    // CSR
    const links = nodeA.length;
    const adjStart = this.adjStart = new Int32Array(count + 1);
    for (let e = 0; e < links; e++) adjStart[nodeA[e] + 1]++;
    for (let n = 0; n < count; n++) adjStart[n + 1] += adjStart[n];
    const adjNode = this.adjNode = new Int32Array(links);
    const adjCost = this.adjCost = new Float32Array(links);
    const adjKind = this.adjKind = new Uint8Array(links);
    {
      const cursor = adjStart.slice(0, count);
      for (let e = 0; e < links; e++) {
        const w = cursor[nodeA[e]]++;
        adjNode[w] = nodeB[e]; adjCost[w] = costL[e]; adjKind[w] = kindL[e];
      }
    }
    this.stats.vaultRays = vaultRays;
    this.stats.linkMs = now() - tl0;
    this.stats.links = links;
    this.stats.offMesh = offMesh;

    // --- pass 4: cover -------------------------------------------------------
    const tv0 = now();
    const coverLow = this.coverLow = new Uint8Array(count);
    const coverHigh = this.coverHigh = new Uint8Array(count);
    const flags = this.flags = new Uint8Array(count);
    const PROBE = 1.05;
    const LOW_Y = 0.62, HIGH_Y = 1.42;
    let coverPoints = 0;
    for (let n = 0; n < count; n++) {
      // only spans that touch a blocked cell can be cover
      const c = col[n];
      const ci = (c / gz) | 0, cj = c % gz;
      let edge = false;
      for (let k = 0; k < 4 && !edge; k++) {
        const ni = ci + NB[k][0], nj = cj + NB[k][1];
        if (ni < 0 || nj < 0 || ni >= gx || nj >= gz) { edge = true; break; }
        if (this._spanNear(ni, nj, py[n], MAX_STEP) < 0) edge = true;
      }
      if (!edge) continue;
      flags[n] = 1;
      let lo = 0, hi = 0;
      for (let d = 0; d < COVER_DIRS; d++) {
        _o.set(px[n], py[n] + LOW_Y, pz[n]);
        _d.set(DIR_X[d], 0, DIR_Z[d]);
        const a = physics.raycast(_o, _d, PROBE, MASK);
        if (!a) continue;
        lo |= (1 << d);
        _o.set(px[n], py[n] + HIGH_Y, pz[n]);
        const b = physics.raycast(_o, _d, PROBE, MASK);
        if (b) hi |= (1 << d);
      }
      coverLow[n] = lo & ~hi;   // low cover = blocked crouched, open standing
      coverHigh[n] = hi;
      if (lo || hi) coverPoints++;
    }
    this.stats.coverMs = now() - tv0;
    this.stats.coverPoints = coverPoints;

    // --- connected components ------------------------------------------------
    // Undirected flood fill. Two spans in different components can never be
    // connected, so an unreachable query is rejected in O(1) instead of
    // expanding the whole graph — that is the only pathological A* case there
    // is on a grid, and it is the one that used to cost hundreds of ms.
    const comp = this.comp = new Int32Array(count).fill(-1);
    {
      const stack = new Int32Array(count);
      let nComp = 0;
      for (let s = 0; s < count; s++) {
        if (comp[s] >= 0) continue;
        let sp = 0;
        stack[sp++] = s; comp[s] = nComp;
        while (sp > 0) {
          const n = stack[--sp];
          for (let e = adjStart[n]; e < adjStart[n + 1]; e++) {
            const m = adjNode[e];
            if (comp[m] < 0) { comp[m] = nComp; stack[sp++] = m; }
          }
        }
        nComp++;
      }
      // one-way links (drops) can leave a region labelled separately from the
      // region above it; merge labels across every link in both directions
      for (let pass = 0; pass < 2; pass++) {
        let changed = false;
        for (let n = 0; n < count; n++) {
          for (let e = adjStart[n]; e < adjStart[n + 1]; e++) {
            const m = adjNode[e];
            if (comp[m] !== comp[n]) {
              const lo = Math.min(comp[m], comp[n]);
              comp[m] = lo; comp[n] = lo; changed = true;
            }
          }
        }
        if (!changed) break;
      }
      this.stats.components = nComp;
    }

    // --- scratch -------------------------------------------------------------
    this._g = new Float32Array(count);
    this._f = new Float32Array(count);
    this._prev = new Int32Array(count);
    this._stamp = new Int32Array(count);
    this._heap = new Heap(Math.max(64, count));
    this._epoch = 0;

    this.stats.rayMs = tRay;
    this.stats.nodes = count;
    this.stats.bakeMs = now() - t0;
    this.ready = count > 0;
    return this.stats;
  }

  /* ---------------------------------------------------------------- *
   * lookups
   * ---------------------------------------------------------------- */

  /** Span index in grid cell (i,j) whose height is within `tol` of y, else -1. */
  _spanNear(i, j, y, tol) {
    if (i < 0 || j < 0 || i >= this.gx || j >= this.gz) return -1;
    const c = i * this.gz + j;
    let best = -1, bestD = tol;
    for (let s = this.colStart[c]; s < this.colStart[c + 1]; s++) {
      const n = this.colNode[s];
      const d = Math.abs(this.py[n] - y);
      if (d <= bestD) { bestD = d; best = n; }
    }
    return best;
  }

  cellOf(x, z) {
    const i = Math.floor((x - this.minX) / this.cell);
    const j = Math.floor((z - this.minZ) / this.cell);
    return (i < 0 || j < 0 || i >= this.gx || j >= this.gz) ? -1 : i * this.gz + j;
  }

  /** Nearest span to a world point, searching outward in rings. */
  nearest(p, maxRings = 6, yTol = 2.2) {
    if (!this.ready) return -1;
    const i0 = Math.floor((p.x - this.minX) / this.cell);
    const j0 = Math.floor((p.z - this.minZ) / this.cell);
    let best = -1, bestD = Infinity;
    for (let r = 0; r <= maxRings; r++) {
      for (let di = -r; di <= r; di++) {
        for (let dj = -r; dj <= r; dj++) {
          if (r > 0 && Math.abs(di) !== r && Math.abs(dj) !== r) continue;
          const i = i0 + di, j = j0 + dj;
          if (i < 0 || j < 0 || i >= this.gx || j >= this.gz) continue;
          const c = i * this.gz + j;
          for (let s = this.colStart[c]; s < this.colStart[c + 1]; s++) {
            const n = this.colNode[s];
            const dy = this.py[n] - p.y;
            if (Math.abs(dy) > yTol) continue;
            const dx = this.px[n] - p.x, dz = this.pz[n] - p.z;
            const d = dx * dx + dz * dz + dy * dy * 4;
            if (d < bestD) { bestD = d; best = n; }
          }
        }
      }
      if (best >= 0 && r >= 1) break;
    }
    return best;
  }

  /** Floor height under (x,z) nearest to `nearY`, or NaN. */
  floorY(x, z, nearY) {
    const c = this.cellOf(x, z);
    if (c < 0) return NaN;
    let best = NaN, bestD = Infinity;
    for (let s = this.colStart[c]; s < this.colStart[c + 1]; s++) {
      const n = this.colNode[s];
      const d = Math.abs(this.py[n] - nearY);
      if (d < bestD) { bestD = d; best = this.py[n]; }
    }
    return best;
  }

  position(n, out = new THREE.Vector3()) {
    return out.set(this.px[n], this.py[n], this.pz[n]);
  }

  /* ---------------------------------------------------------------- *
   * A*
   * ---------------------------------------------------------------- */

  _h(a, b) {
    const dx = this.px[a] - this.px[b];
    const dy = this.py[a] - this.py[b];
    const dz = this.pz[a] - this.pz[b];
    return Math.sqrt(dx * dx + dz * dz) + Math.abs(dy) * 1.4;
  }

  /**
   * Weighted A* between two spans.
   *
   * The heuristic is inflated (`HEURISTIC_W`): on a dense 8-connected grid an
   * admissible heuristic explores a huge plateau of equal-cost routes, and the
   * few percent of extra path length costs an order of magnitude fewer
   * expansions. Games take that trade every time. The expansion cap bounds the
   * worst case; a capped search returns its best partial route toward the goal
   * so the agent still commits to a sensible move and re-paths from there.
   *
   * @returns {number[]} span indices start..goal, or [] when unreachable.
   */
  search(start, goal, maxExpand = 1400) {
    if (!this.ready || start < 0 || goal < 0) return [];
    if (start === goal) return [start];
    if (this.comp && this.comp[start] !== this.comp[goal]) return [];
    const t0 = now();
    const g = this._g, f = this._f, prev = this._prev, stamp = this._stamp;
    const epoch = ++this._epoch;
    const heap = this._heap;
    heap.clear();
    g[start] = 0; f[start] = this._h(start, goal); prev[start] = -1; stamp[start] = epoch;
    heap.push(start, f[start]);
    let expanded = 0;
    let found = false;
    let bestNode = start, bestH = f[start];
    const closed = this._closed || (this._closed = new Int32Array(this.count));
    while (heap.n > 0 && expanded < maxExpand) {
      const n = heap.pop();
      if (n === goal) { found = true; bestNode = goal; break; }
      if (closed[n] === epoch) continue;
      closed[n] = epoch;
      expanded++;
      for (let e = this.adjStart[n]; e < this.adjStart[n + 1]; e++) {
        const m = this.adjNode[e];
        if (closed[m] === epoch) continue;
        const ng = g[n] + this.adjCost[e];
        if (stamp[m] === epoch && ng >= g[m]) continue;
        stamp[m] = epoch;
        g[m] = ng; prev[m] = n;
        const h = this._h(m, goal);
        if (h < bestH) { bestH = h; bestNode = m; }
        heap.push(m, ng + h * HEURISTIC_W);
      }
    }
    this._queryMs += now() - t0;
    this._queries++;
    this._lastExpanded = expanded;
    this._lastPartial = !found;
    // A capped or blocked search still yields the closest approach found.
    if (!found && bestNode === start) return [];
    const out = [];
    for (let n = bestNode; n >= 0; n = prev[n]) {
      out.push(n);
      if (n === start) break;
    }
    out.reverse();
    return out;
  }

  /** Span at a world point whose height is near `y`, else -1. */
  spanAt(x, z, y, tol = 0.62) {
    const c = this.cellOf(x, z);
    if (c < 0) return -1;
    let best = -1, bestD = tol;
    for (let k = this.colStart[c]; k < this.colStart[c + 1]; k++) {
      const n = this.colNode[k];
      const d = Math.abs(this.py[n] - y);
      if (d < bestD) { bestD = d; best = n; }
    }
    return best;
  }

  /**
   * Can an agent walk the straight segment a->b?
   *
   * Sampled entirely against the baked spans, so this costs no physics queries.
   * A centre sample alone lets a diagonal clip a corner, so each sample also
   * probes one agent-radius to either side: the whole swept capsule has to be
   * over walkable ground, not just its axis.
   */
  clearWalk(ax, ay, az, bx, by, bz) {
    const dx = bx - ax, dz = bz - az, dy = by - ay;
    const len = Math.hypot(dx, dz);
    if (len < 1e-4) return true;
    const inv = 1 / len;
    const perpX = -dz * inv * (AGENT_RADIUS + 0.06);
    const perpZ = dx * inv * (AGENT_RADIUS + 0.06);
    const steps = Math.max(2, Math.ceil(len / (this.cell * 0.55)));
    let py = ay;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const x = ax + dx * t, z = az + dz * t;
      const wantY = ay + dy * t;
      const c = this.spanAt(x, z, wantY);
      if (c < 0) return false;
      if (this.spanAt(x + perpX, z + perpZ, wantY) < 0) return false;
      if (this.spanAt(x - perpX, z - perpZ, wantY) < 0) return false;
      if (Math.abs(this.py[c] - py) > MAX_STEP) return false;
      py = this.py[c];
    }
    return true;
  }

  /**
   * Full path query: world point -> world point, string-pulled.
   * @returns {{points: THREE.Vector3[], kinds: number[], ms: number, nodes: number[]}}
   */
  findPath(from, to, out = null) {
    const res = out || { points: [], kinds: [], nodes: [], ms: 0, ok: false, partial: false };
    res.points.length = 0; res.kinds.length = 0; res.nodes.length = 0;
    res.ok = false; res.partial = false;
    const t0 = now();
    const a = this.nearest(from);
    const b = this.nearest(to);
    if (a < 0 || b < 0) { res.ms = now() - t0; return res; }
    let raw = this.search(a, b);
    if (raw.length === 0) { res.ms = now() - t0; return res; }
    // A capped search can return a very long chain; an agent only ever walks
    // the first stretch before re-pathing, and smoothing the rest is pure cost.
    res.partial = this._lastPartial || raw.length > MAX_RAW_NODES;
    if (raw.length > MAX_RAW_NODES) raw = raw.slice(0, MAX_RAW_NODES);
    res.nodes = raw;

    // string pull: greedily skip nodes we can walk straight to, but never skip
    // across an off-mesh link (a vault or a drop must be traversed explicitly).
    const kindTo = new Uint8Array(raw.length);
    for (let i = 1; i < raw.length; i++) kindTo[i] = this._kindBetween(raw[i - 1], raw[i]);

    let i = 0;
    res.points.push(this.position(raw[0]));
    res.kinds.push(LINK_WALK);
    while (i < raw.length - 1) {
      let j = i + 1;
      let best = i + 1;
      // Bounded look-ahead: without it the smoother is O(n^2) in clearWalk and
      // a long path costs more than the search that produced it.
      const limit = Math.min(raw.length, i + 1 + SMOOTH_LOOKAHEAD);
      while (j < limit) {
        if (kindTo[j] !== LINK_WALK) { best = j; break; }
        if (!this.clearWalk(
          this.px[raw[i]], this.py[raw[i]], this.pz[raw[i]],
          this.px[raw[j]], this.py[raw[j]], this.pz[raw[j]])) break;
        best = j;
        j++;
      }
      res.points.push(this.position(raw[best]));
      res.kinds.push(kindTo[best]);
      i = best;
    }
    res.ok = true;
    res.ms = now() - t0;
    return res;
  }

  _kindBetween(a, b) {
    for (let e = this.adjStart[a]; e < this.adjStart[a + 1]; e++) {
      if (this.adjNode[e] === b) return this.adjKind[e];
    }
    return LINK_WALK;
  }

  /* ---------------------------------------------------------------- *
   * cover queries
   * ---------------------------------------------------------------- */

  /** Does span n protect from a threat lying in world direction (dx,dz)? */
  coverFrom(n, dx, dz) {
    const d = dirBin(dx, dz);
    const lo = this.coverLow[n], hi = this.coverHigh[n];
    // neighbouring bins count at half weight — a wall rarely lines up exactly
    const dl = (d + COVER_DIRS - 1) % COVER_DIRS, dr = (d + 1) % COVER_DIRS;
    let s = 0;
    if (hi & (1 << d)) s = 2; else if (lo & (1 << d)) s = 1;
    let side = 0;
    if (hi & (1 << dl)) side = Math.max(side, 2); else if (lo & (1 << dl)) side = Math.max(side, 1);
    if (hi & (1 << dr)) side = Math.max(side, 2); else if (lo & (1 << dr)) side = Math.max(side, 1);
    return s > 0 ? s : side * 0.5;
  }

  /**
   * Iterate cover spans in a world-space radius. `fn(n)` per span.
   */
  forEachCoverNear(x, z, y, radius, fn) {
    const r = Math.ceil(radius / this.cell);
    const i0 = Math.floor((x - this.minX) / this.cell);
    const j0 = Math.floor((z - this.minZ) / this.cell);
    const r2 = radius * radius;
    for (let i = i0 - r; i <= i0 + r; i++) {
      if (i < 0 || i >= this.gx) continue;
      for (let j = j0 - r; j <= j0 + r; j++) {
        if (j < 0 || j >= this.gz) continue;
        const c = i * this.gz + j;
        for (let s = this.colStart[c]; s < this.colStart[c + 1]; s++) {
          const n = this.colNode[s];
          if (!this.coverLow[n] && !this.coverHigh[n]) continue;
          if (Math.abs(this.py[n] - y) > 3.0) continue;
          const dx = this.px[n] - x, dz = this.pz[n] - z;
          if (dx * dx + dz * dz > r2) continue;
          fn(n);
        }
      }
    }
  }

  /** Iterate every span in a radius, cover or not. */
  forEachNear(x, z, y, radius, fn) {
    const r = Math.ceil(radius / this.cell);
    const i0 = Math.floor((x - this.minX) / this.cell);
    const j0 = Math.floor((z - this.minZ) / this.cell);
    const r2 = radius * radius;
    for (let i = i0 - r; i <= i0 + r; i++) {
      if (i < 0 || i >= this.gx) continue;
      for (let j = j0 - r; j <= j0 + r; j++) {
        if (j < 0 || j >= this.gz) continue;
        const c = i * this.gz + j;
        for (let s = this.colStart[c]; s < this.colStart[c + 1]; s++) {
          const n = this.colNode[s];
          if (Math.abs(this.py[n] - y) > 3.0) continue;
          const dx = this.px[n] - x, dz = this.pz[n] - z;
          if (dx * dx + dz * dz > r2) continue;
          fn(n);
        }
      }
    }
  }

  queryStats() {
    return {
      queries: this._queries,
      totalMs: this._queryMs,
      avgMs: this._queries ? this._queryMs / this._queries : 0,
    };
  }
}

/* ------------------------------------------------------------------ *
 * Local steering — force based avoidance, deliberately cheap.
 * ------------------------------------------------------------------ */

const _steer = new THREE.Vector3();
const _away = new THREE.Vector3();

/**
 * Blend a path-following velocity with separation from other agents and a
 * wall push derived from the nav grid. Writes into `out`.
 *
 * @param {THREE.Vector3} out
 * @param {THREE.Vector3} pos      agent feet
 * @param {THREE.Vector3} target   next corner
 * @param {number} speed
 * @param {Array} others           other agents ({ pos, radius, vel })
 * @param {NavGraph} nav
 */
export function steer(out, pos, target, speed, others, nav, selfIndex) {
  _steer.set(target.x - pos.x, 0, target.z - pos.z);
  const dist = _steer.length();
  if (dist > 1e-4) _steer.multiplyScalar(speed / dist);
  else _steer.set(0, 0, 0);

  // --- agent separation (reciprocal, so pairs do not fight) ---
  for (let i = 0; i < others.length; i++) {
    if (i === selfIndex) continue;
    const o = others[i];
    if (!o || !o.alive) continue;
    const dx = pos.x - o.pos.x, dz = pos.z - o.pos.z;
    const d2 = dx * dx + dz * dz;
    const want = 0.95;
    if (d2 > want * want || d2 < 1e-6) continue;
    const d = Math.sqrt(d2);
    const push = (want - d) / want;
    _away.set(dx / d, 0, dz / d);
    // a small tangential bias breaks the head-on deadlock
    const tx = -_away.z, tz = _away.x;
    const side = (selfIndex < i) ? 1 : -1;
    _steer.x += (_away.x * 1.9 + tx * side * 0.9) * push * speed;
    _steer.z += (_away.z * 1.9 + tz * side * 0.9) * push * speed;
  }

  // --- wall push from the nav grid: steer back toward walkable space ---
  if (nav && nav.ready) {
    const c = nav.cell;
    for (let k = 0; k < 4; k++) {
      const ox = k === 0 ? c : k === 1 ? -c : 0;
      const oz = k === 2 ? c : k === 3 ? -c : 0;
      const cell = nav.cellOf(pos.x + ox, pos.z + oz);
      let open = false;
      if (cell >= 0) {
        for (let s = nav.colStart[cell]; s < nav.colStart[cell + 1]; s++) {
          if (Math.abs(nav.py[nav.colNode[s]] - pos.y) < MAX_STEP + 0.2) { open = true; break; }
        }
      }
      if (!open) { _steer.x -= ox * speed * 0.55 / c; _steer.z -= oz * speed * 0.55 / c; }
    }
  }

  const l = Math.hypot(_steer.x, _steer.z);
  if (l > speed) { _steer.x *= speed / l; _steer.z *= speed / l; }
  out.set(_steer.x, 0, _steer.z);
  return out;
}

function now() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}
